import type { OpsBet, OpsData, OpsPlayer, OpsRound, OpsTx } from './model.ts';

/** Portal filters. Kept in the URL so every view (and its CSV) is shareable and reproducible. */
export interface OpsFilters {
  from: string | null;
  to: string | null;
  game: string | null;
  camera: string | null;
  operator: string | null;
  brand: string | null;
  currency: string | null;
}

export const FILTER_KEYS = ['from', 'to', 'game', 'camera', 'operator', 'brand', 'currency'] as const;

export const EMPTY_FILTERS: OpsFilters = { from: null, to: null, game: null, camera: null, operator: null, brand: null, currency: null };

export function filtersFromSearch(search: string): OpsFilters {
  const p = new URLSearchParams(search);
  const f = { ...EMPTY_FILTERS };
  for (const k of FILTER_KEYS) {
    const v = p.get(k);
    f[k] = v && v.length <= 64 ? v : null;
  }
  return f;
}

export function filtersToSearch(f: OpsFilters, extra: Record<string, string | null> = {}): string {
  const p = new URLSearchParams();
  for (const k of FILTER_KEYS) if (f[k]) p.set(k, f[k]!);
  for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
}

const ms = (iso: string | null) => (iso ? Date.parse(iso) : NaN);
function inRange(at: string, f: OpsFilters): boolean {
  const t = Date.parse(at);
  const a = ms(f.from);
  const b = ms(f.to);
  return (Number.isNaN(a) || t >= a) && (Number.isNaN(b) || t < b);
}

export interface Filtered {
  rounds: OpsRound[];
  bets: OpsBet[];
  txs: OpsTx[];
  players: OpsPlayer[];
}

/** Applies the filters consistently to every record type (a tx follows its bet's game/camera). */
export function applyFilters(d: OpsData, f: OpsFilters): Filtered {
  const roundById = new Map(d.rounds.map((r) => [r.gameRoundId, r]));
  const tenantCur = new Map(d.tenants.map((t) => [t.brandId, t.currency.code]));
  const roundOk = (r: OpsRound | undefined) => !!r && (!f.game || r.gameId === f.game) && (!f.camera || r.cameraAlias === f.camera);
  const tenantOk = (x: { operatorId: string; brandId: string }) =>
    (!f.operator || x.operatorId === f.operator) && (!f.brand || x.brandId === f.brand) && (!f.currency || tenantCur.get(x.brandId) === f.currency);
  const rounds = d.rounds.filter((r) => roundOk(r) && inRange(r.manifest.timing.bettingOpenAt, f));
  const bets = d.bets.filter((b) => tenantOk(b) && roundOk(roundById.get(b.bet.gameRoundId)) && inRange(b.bet.submittedAt, f));
  const betByKey = new Map(d.bets.map((b) => [b.key, b]));
  const noBetScope = !f.game && !f.camera;
  // A transaction is filtered by its own time and tenant, and by its bet's game/camera.
  const txs = d.txs.filter(
    (x) => tenantOk(x) && inRange(x.at, f) && (noBetScope || (x.betKey !== null && roundOk(roundById.get(betByKey.get(x.betKey)?.bet.gameRoundId ?? '')))),
  );
  const players = d.players.filter(tenantOk);
  return { rounds, bets, txs, players };
}
